import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardMedia,
  Chip,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Skeleton,
  CardContent,
  InputAdornment,
} from '@mui/material';
import { Article, TrendingUp, Bookmark, Movie as MovieIcon, ChevronLeft, ChevronRight, GridView, ViewCarousel, FilterList, Apps } from '@mui/icons-material';
import NewsCard from './NewsCard';
import NewsSearch from './NewsSearch';
import GridNewsView from './InstagramNewsGrid';

import optimizedNewsApi from '../../services/optimizedNewsApi';
import { getMoviesByIds } from '../../services/mongoApi';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { showAuthAlert } from '../../utils/authAlerts';
import { useNavigate, useSearchParams } from 'react-router-dom';

const NewsPage = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchActive, setSearchActive] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [movieDetails, setMovieDetails] = useState({});
  const loadedMoviesRef = useRef(new Set());
  const [sortBy, setSortBy] = useState('latest');
  const [viewMode, setViewMode] = useState('scroll');
  const [displayLimit, setDisplayLimit] = useState(15);
  const [dateFilter, setDateFilter] = useState('all');
  const loadMoreRef = useRef(null);
  const { user } = useAuth();
  const { addNotification } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const movieId = searchParams.get('movie');

  // Preload news data on component mount
  useEffect(() => {
    optimizedNewsApi.preloadNews();
  }, []);

  useEffect(() => {
    loadNews();
  }, [selectedTab, user, movieId]);

  const loadNews = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let result;
      
      if (movieId) {
        result = await optimizedNewsApi.getMovieNews(movieId);
        // If no results and not from cache, movie might not have news
        if (!result.success || (result.items.length === 0 && !result.fromCache)) {
          console.log('No news found for movie:', movieId);
        }
      } else {
        switch (selectedTab) {
          case 0:
            result = await optimizedNewsApi.getPublishedNews({ limit: 50 });
            break;
          case 1:
            result = await optimizedNewsApi.getFeaturedNews();
            break;
          default:
            result = await optimizedNewsApi.getPublishedNews({ limit: 50 });
        }
      }

      if (result.success) {
        const items = result.items || [];
        setNews(items);
        
        // Show cache indicator
        if (result.fromCache) {
          console.log('Loaded from cache - instant!');
        }
        
        // Lazy load movie details (non-blocking)
        loadMovieDetails(items);
      } else {
        setError(result.error || 'Failed to load news');
      }
    } catch (err) {
      setError('Failed to load news');
      console.error('Error loading news:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMovieDetails = async (newsItems) => {
    const uniqueTmdbIds = [...new Set(newsItems.filter(n => n.tmdb_id).map(n => n.tmdb_id))];
    const unloadedIds = uniqueTmdbIds.filter(id => !loadedMoviesRef.current.has(id));
    
    if (unloadedIds.length === 0) return;
    
    // Mark as loading to prevent duplicates
    unloadedIds.forEach(id => loadedMoviesRef.current.add(id));
    
    // Batch fetch all movie details
    try {
      const details = await getMoviesByIds(unloadedIds);
      setMovieDetails(prev => ({ ...prev, ...details }));
    } catch (err) {
      console.error('Error loading movie details:', err);
      // Remove from loaded set on error so it can be retried
      unloadedIds.forEach(id => loadedMoviesRef.current.delete(id));
    }
  };

  const scrollRefs = useRef({});
  const [scrollPositions, setScrollPositions] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [groupSorts, setGroupSorts] = useState({});

  const displayNews = searchActive ? searchResults : news;
  
  // Apply date filter
  const filteredNews = useMemo(() => {
    if (dateFilter === 'all') return displayNews;
    
    const now = new Date();
    const filterDate = new Date();
    
    switch (dateFilter) {
      case 'today':
        filterDate.setHours(0, 0, 0, 0);
        break;
      case 'yesterday':
        filterDate.setDate(now.getDate() - 1);
        filterDate.setHours(0, 0, 0, 0);
        break;
      case 'week':
        filterDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        filterDate.setMonth(now.getMonth() - 1);
        break;
      case 'year':
        filterDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        return displayNews;
    }
    
    return displayNews.filter(item => new Date(item.published_at) >= filterDate);
  }, [displayNews, dateFilter]);
  
  const paginatedNews = filteredNews.slice(0, displayLimit);

  const groupedNews = useMemo(() => {
    // Step 1: Separate grouped and ungrouped (no global sort)
    const grouped = {};
    const ungrouped = [];

    paginatedNews.forEach(item => {
      if (item.tmdb_id) {
        if (!grouped[item.tmdb_id]) {
          grouped[item.tmdb_id] = [];
        }
        grouped[item.tmdb_id].push(item);
      } else {
        ungrouped.push(item);
      }
    });

    // Step 2: Sort articles within each group based on group-specific sort
    Object.keys(grouped).forEach(tmdbId => {
      const groupSort = groupSorts[tmdbId] || 'latest';
      grouped[tmdbId].sort((a, b) => {
        const dateA = new Date(a.published_at);
        const dateB = new Date(b.published_at);
        return groupSort === 'latest' ? dateB - dateA : dateA - dateB;
      });
    });

    // Step 3: Create timeline ordered by published_at (groups by their ORIGINAL first article date)
    const timeline = [];
    const groupEntries = Object.entries(grouped).map(([tmdbId, articles]) => {
      // Find the latest article date from the ORIGINAL unsorted list to maintain group position
      const originalArticles = paginatedNews.filter(item => item.tmdb_id === tmdbId);
      const latestDate = Math.max(...originalArticles.map(a => new Date(a.published_at)));
      
      return {
        type: 'group',
        tmdbId,
        articles, // These are sorted by group-specific sort
        publishedDate: new Date(latestDate) // Use latest date for group positioning
      };
    });

    // Sort groups by published date (latest article in group)
    groupEntries.sort((a, b) => b.publishedDate - a.publishedDate);

    // Intersperse ungrouped news chronologically
    let groupIndex = 0;
    let ungroupedIndex = 0;

    // Sort ungrouped by published date
    ungrouped.sort((a, b) => new Date(b.published_at) - new Date(a.published_at));

    while (groupIndex < groupEntries.length || ungroupedIndex < ungrouped.length) {
      const currentGroup = groupEntries[groupIndex];
      const currentUngrouped = ungrouped[ungroupedIndex];

      if (!currentGroup) {
        timeline.push({ type: 'ungrouped', article: currentUngrouped });
        ungroupedIndex++;
      } else if (!currentUngrouped) {
        timeline.push(currentGroup);
        groupIndex++;
      } else {
        const groupDate = currentGroup.publishedDate;
        const ungroupedDate = new Date(currentUngrouped.published_at);
        
        if (groupDate >= ungroupedDate) {
          timeline.push(currentGroup);
          groupIndex++;
        } else {
          timeline.push({ type: 'ungrouped', article: currentUngrouped });
          ungroupedIndex++;
        }
      }
    }

    console.log('groupedNews timeline created:', timeline.length, 'items');
    return timeline;
  }, [paginatedNews, groupSorts]);

  const handleSearch = async (searchParams) => {
    setLoading(true);
    setError(null);
    try {
      const result = await optimizedNewsApi.searchNews(searchParams);
      
      if (result.success) {
        setSearchResults(result.items);
        setSearchActive(true);
        
        if (result.items.length === 0) {
          addNotification('No results found. Try different search terms.', 'info');
        } else {
          addNotification(`Found ${result.items.length} results`, 'success');
        }
      } else {
        setError(result.error || 'Search failed');
        addNotification('Search failed. Please try again.', 'error');
      }
    } catch (err) {
      setError('Search failed');
      addNotification('Search failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchActive(false);
    setSearchResults([]);
    addNotification('Search cleared', 'info');
  };



  const toggleGroup = (tmdbId) => {
    setExpandedGroups(prev => ({ ...prev, [tmdbId]: !prev[tmdbId] }));
  };

  const handleScroll = (tmdbId, direction) => {
    const container = scrollRefs.current[tmdbId];
    if (!container) return;
    
    const scrollAmount = 340;
    const newScrollLeft = direction === 'left' 
      ? container.scrollLeft - scrollAmount 
      : container.scrollLeft + scrollAmount;
    
    container.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
  };

  const updateScrollPosition = (tmdbId) => {
    const container = scrollRefs.current[tmdbId];
    if (!container) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setScrollPositions(prev => ({
      ...prev,
      [tmdbId]: {
        atStart: scrollLeft <= 0,
        atEnd: scrollLeft + clientWidth >= scrollWidth - 1
      }
    }));
  };

  const loadMore = useCallback(() => {
    setDisplayLimit(prev => Math.min(prev + 15, displayNews.length));
  }, [displayNews.length]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && displayLimit < displayNews.length) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [displayLimit, displayNews.length, loadMore]);

  const tabs = [
    { label: 'All News', icon: <Article /> },
    { label: 'Featured', icon: <TrendingUp /> },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 2,  }}>
      <Box mb={2}>
        <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Movie News
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Stay updated with the latest movie news
        </Typography>
      </Box>

      <Box mb={2}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => {
            if (tabs[newValue].requiresAuth && !user) {
              showAuthAlert(addNotification, navigate, 'NEWS_BOOKMARKS');
              return;
            }
            setSelectedTab(newValue);
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ '& .MuiTab-root': { minHeight: 40, fontSize: '0.875rem' } }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              icon={tab.icon}
              label={tab.label}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Box>

      {/* Search */}
      {selectedTab === 0 && (
        <Box>
          <NewsSearch
            onSearch={handleSearch}
            onClear={handleClearSearch}
            category="movies"
          />
          {!loading && displayNews.length > 0 && (
            <Box display="flex" gap={1.5} mb={2} alignItems="center" justifyContent="center" flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterList sx={{ fontSize: 16, ml: 0.5 }} />
                    </InputAdornment>
                  }
                  sx={{
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    fontSize: '0.875rem',
                    height: 36
                  }}
                >
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="yesterday">Yesterday</MenuItem>
                  <MenuItem value="week">This Week</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                  <MenuItem value="year">This Year</MenuItem>
                </Select>
              </FormControl>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => newMode && setViewMode(newMode)}
                size="small"
                sx={{ height: 36 }}
              >
                <ToggleButton value="scroll" sx={{ fontSize: '0.75rem', px: 1.5 }}>
                  <ViewCarousel sx={{ mr: 0.5, fontSize: 16 }} /> Scroll
                </ToggleButton>
                {/* <ToggleButton value="grid">
                  <GridView sx={{ mr: 0.5 }} /> Grid
                </ToggleButton> */}
                <ToggleButton value="instagram" sx={{ fontSize: '0.75rem', px: 1.5 }}>
                  <Apps sx={{ mr: 0.5, fontSize: 16 }} /> Grid
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
          {searchActive && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Showing {searchResults.length} search result{searchResults.length !== 1 ? 's' : ''}
            </Alert>
          )}
        </Box>
      )}



      {loading ? (
        <Box display="flex" flexDirection="column" gap={3}>
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <Box display="flex" gap={2} p={2}>
                <Skeleton variant="rectangular" width={64} height={96} sx={{ borderRadius: 1 }} />
                <Box flex={1}>
                  <Skeleton variant="text" width="60%" height={32} />
                  <Skeleton variant="text" width="40%" height={24} />
                </Box>
              </Box>
              <Box display="flex" gap={2} p={2} overflow="hidden">
                {[1, 2, 3].map((j) => (
                  <Box key={j} sx={{ minWidth: 320, flexShrink: 0 }}>
                    <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 1, mb: 1 }} />
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="60%" />
                  </Box>
                ))}
              </Box>
            </Card>
          ))}
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button onClick={loadNews} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      ) : (!searchActive && displayNews.length === 0) ? (
        <Alert severity="info">
          No news articles available at the moment. Check back soon!
        </Alert>
      ) : (searchActive && displayNews.length === 0) ? (
        <Alert severity="info">
          No results found. Try different search terms.
        </Alert>
      ) : viewMode === 'instagram' ? (
        <Box>
          {/* Instagram Grid View - Show all movie groups as poster grid */}
          <GridNewsView 
            groupedNews={Object.fromEntries(
              groupedNews
                .filter(item => item.type === 'group')
                .map(item => [item.tmdbId, item.articles])
            )} 
            movieDetails={movieDetails} 
          />
          
          {/* Ungrouped articles below */}
          {(() => {
            const ungroupedArticles = groupedNews
              .filter(item => item.type === 'ungrouped')
              .map(item => item.article);
            
            if (ungroupedArticles.length === 0) return null;
            
            return (
              <Box mt={3}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                  Other News
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      md: 'repeat(auto-fit, minmax(300px, 1fr))',
                    },
                    gap: 2,
                  }}
                >
                  {ungroupedArticles.map((article) => (
                    <NewsCard
                      key={article.id}
                      news={article}
                      gridView
                      onClick={() => navigate(`/news/${article.id}`)}
                    />
                  ))}
                </Box>
              </Box>
            );
          })()}
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={3}>
          {/* Hybrid timeline */}
          {(() => {
            const ungroupedBuffer = [];
            const result = [];

            groupedNews.forEach((item, index) => {
              if (item.type === 'ungrouped') {
                ungroupedBuffer.push(item.article);
              } else {
                // Flush ungrouped buffer before group
                if (ungroupedBuffer.length > 0) {
                  result.push(
                    <Box
                      key={`ungrouped-batch-${index}`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: 'repeat(2, 1fr)',
                          md: 'repeat(auto-fit, minmax(300px, 1fr))',
                        },
                        gap: 2,
                      }}
                    >
                      {ungroupedBuffer.map((article) => (
                        <NewsCard
                          key={article.id}
                          news={article}
                          gridView
                          onClick={() => navigate(`/news/${article.id}`)}
                        />
                      ))}
                    </Box>
                  );
                  ungroupedBuffer.length = 0;
                }

                // Add group
                const { tmdbId, articles: newsItems } = item;
                const groupSort = groupSorts[tmdbId] || 'latest';
                const movie = movieDetails[tmdbId];
                const scrollPos = scrollPositions[tmdbId] || { atStart: true, atEnd: false };
                const isExpanded = expandedGroups[tmdbId];
                
                result.push(
                  <Card key={`group-${tmdbId}`} sx={{ overflow: 'visible', bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
                <Box 
                  display="flex" 
                  alignItems="center" 
                  gap={2} 
                  p={2} 
                  sx={{ 
                    borderBottom: isExpanded ? '1px solid' : 'none',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={() => toggleGroup(tmdbId)}
                >
                  {movie?.poster ? (
                    <CardMedia
                      component="img"
                      image={movie.poster}
                      alt={movie.name}
                      sx={{ 
                        width: { xs: 48, md: 64 }, 
                        height: { xs: 72, md: 96 }, 
                        borderRadius: 1, 
                        objectFit: 'cover',
                        flexShrink: 0
                      }}
                      onClick={(e) => { e.stopPropagation(); navigate(`/movies/${tmdbId}`); }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: { xs: 48, md: 64 },
                        height: { xs: 72, md: 96 },
                        borderRadius: 1,
                        bgcolor: 'grey.300',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <MovieIcon sx={{ fontSize: 32, color: 'grey.500' }} />
                    </Box>
                  )}
                  <Box flex={1}>
                    <Typography 
                      variant="h6" 
                      fontWeight={700}
                      sx={{ mb: 0.5 }}
                    >
                      {movie?.name || 'Movie News'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {movie?.release_date && `${new Date(movie.release_date).getFullYear()} · `}
                      {newsItems.length} article{newsItems.length > 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <Chip 
                    label={`${newsItems.length} article${newsItems.length > 1 ? 's' : ''}`} 
                    size="small"
                    sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' } }}
                  />
                  <FormControl 
                    size="small" 
                    sx={{ 
                      minWidth: { xs: 100, sm: 120 }, 
                      flexShrink: 0 
                    }} 
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Select
                      value={groupSort}
                      onChange={(e) => {
                        e.stopPropagation();
                        setGroupSorts(prev => ({ ...prev, [tmdbId]: e.target.value }));
                      }}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      <MenuItem value="latest">Latest</MenuItem>
                      <MenuItem value="oldest">Oldest</MenuItem>
                    </Select>
                  </FormControl>
                  <IconButton size="small" sx={{ flexShrink: 0 }}>
                    {isExpanded ? <ChevronLeft sx={{ transform: 'rotate(90deg)' }} /> : <ChevronRight sx={{ transform: 'rotate(90deg)' }} />}
                  </IconButton>
                </Box>

                {isExpanded && (
                  viewMode === 'grid' ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: 'repeat(auto-fill, minmax(280px, 1fr))',
                        },
                        gap: 2,
                        p: 2,
                      }}
                    >
                      {newsItems.map((item) => (
                        <NewsCard key={item.id} news={item} gridView onClick={() => navigate(`/news/${item.id}`)} />
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ position: 'relative' }}>
                      {!scrollPos.atStart && (
                        <IconButton
                          onClick={() => handleScroll(tmdbId, 'left')}
                          sx={{
                            position: 'absolute',
                            left: -20,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 2,
                            bgcolor: 'background.paper',
                            boxShadow: 2,
                            '&:hover': { bgcolor: 'background.paper' }
                          }}
                        >
                          <ChevronLeft />
                        </IconButton>
                      )}
                      {!scrollPos.atEnd && newsItems.length > 1 && (
                        <IconButton
                          onClick={() => handleScroll(tmdbId, 'right')}
                          sx={{
                            position: 'absolute',
                            right: -20,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 2,
                            bgcolor: 'background.paper',
                            boxShadow: 2,
                            '&:hover': { bgcolor: 'background.paper' }
                          }}
                        >
                          <ChevronRight />
                        </IconButton>
                      )}
                      <Box 
                        ref={el => scrollRefs.current[tmdbId] = el}
                        onScroll={() => updateScrollPosition(tmdbId)}
                        sx={{ 
                          display: 'flex',
                          gap: 2,
                          p: 1.5,
                          overflowX: 'auto',
                          scrollbarWidth: 'none',
                          '&::-webkit-scrollbar': { display: 'none' }
                        }}
                      >
                        {newsItems.map((item) => (
                          <Box 
                            key={item.id} 
                            sx={{ 
                              minWidth: { xs: 280, sm: 320 },
                              flexShrink: 0
                            }}
                          >
                            <NewsCard news={item} compact onClick={() => navigate(`/news/${item.id}`)} />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )
                )}
                  </Card>
                );
              }
            });

            // Flush remaining ungrouped articles
            if (ungroupedBuffer.length > 0) {
              result.push(
                <Box
                  key="ungrouped-batch-final"
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      md: 'repeat(auto-fit, minmax(300px, 1fr))',
                    },
                    gap: 2,
                  }}
                >
                  {ungroupedBuffer.map((article) => (
                    <NewsCard
                      key={article.id}
                      news={article}
                      gridView
                      onClick={() => navigate(`/news/${article.id}`)}
                    />
                  ))}
                </Box>
              );
            }

            return result;
          })()}

          {/* Load More Trigger */}
          {displayLimit < displayNews.length && (
            <Box ref={loadMoreRef} sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Loading more articles... ({displayLimit} of {displayNews.length})
              </Typography>
            </Box>
          )}

          {/* Load More Button (fallback) */}
          {displayLimit < displayNews.length && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button 
                variant="outlined" 
                onClick={loadMore}
                size="large"
              >
                Load More ({displayNews.length - displayLimit} remaining)
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
};

export default NewsPage;